import { FormEvent, useState } from 'react'
import { useAuth } from '../hooks/useAuth'

interface Props {
  onSuccess?: () => void
}

export function LoginPage({ onSuccess }: Props) {
  const { login } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await login(username, password)
      onSuccess?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">CAP Alert Platform</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Sign in to continue</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-800 rounded-xl p-8 space-y-5 shadow-xl"
        >
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-300 text-red-700 dark:bg-red-950 dark:border-red-800 dark:text-red-300 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-1">
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Username
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-lg bg-gray-100 border border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white
                         placeholder-gray-500 text-sm focus:outline-none focus:ring-2
                         focus:ring-blue-500 focus:border-transparent transition"
              placeholder="admin"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg bg-gray-100 border border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white
                         placeholder-gray-500 text-sm focus:outline-none focus:ring-2
                         focus:ring-blue-500 focus:border-transparent transition"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-blue-200 dark:disabled:bg-blue-900
                       disabled:text-blue-500 dark:disabled:text-blue-400 text-white text-sm font-semibold py-2.5 transition
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                       focus:ring-offset-gray-50 dark:focus:ring-offset-gray-900"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
